import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '../button/button.component';
import { CheckboxComponent } from '../form/checkbox.component';
import { copyToClipboard } from '../util/clipboard';

/** Nome del file scaricato con i codici di recupero. */
export const RECOVERY_CODES_FILE_NAME = 'codici-recupero-bsite.txt';

/** Testo del file dei codici: un'intestazione breve in italiano, la data e un codice per riga. */
export function recoveryCodesText(codes: readonly string[], date: Date = new Date()): string {
  const day = new Intl.DateTimeFormat('it-IT', { dateStyle: 'long' }).format(date);
  return [
    'B.Site - Codici di recupero',
    `Generati il ${day}.`,
    'Ogni codice si può usare una sola volta, al posto del codice dell’app di autenticazione.',
    'Conservali in un posto sicuro.',
    '',
    ...codes,
    '',
  ].join('\n');
}

/**
 * Codici di recupero mostrati una sola volta: griglia in monospazio, "Copia", "Scarica" (file
 * `.txt`) e "Stampa" (una pagina pulita con i soli codici), più la casella "Ho conservato i
 * codici in un posto sicuro" legata a `confirmed`.
 */
@Component({
  selector: 'bs-recovery-codes',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ButtonComponent, CheckboxComponent],
  host: { class: 'flex flex-col gap-4' },
  template: `
    <ol
      class="grid grid-cols-2 gap-2 rounded-2xl bg-stone-100 p-4 font-mono text-base text-ink dark:bg-stone-800 dark:text-stone-50"
      aria-label="Codici di recupero"
      [attr.data-testid]="testId()"
    >
      @for (code of codes(); track code) {
        <li class="select-all text-center">{{ code }}</li>
      }
    </ol>
    <div class="flex flex-wrap gap-2">
      <bs-button
        variant="secondary"
        icon="copy"
        [testId]="testId() ? testId() + '-copy' : null"
        (click)="copy()"
      >
        Copia
      </bs-button>
      <bs-button
        variant="secondary"
        icon="download"
        [testId]="testId() ? testId() + '-download' : null"
        (click)="download()"
      >
        Scarica
      </bs-button>
      <bs-button
        variant="secondary"
        icon="printer"
        [testId]="testId() ? testId() + '-print' : null"
        (click)="print()"
      >
        Stampa
      </bs-button>
    </div>
    <p class="min-h-5 text-sm text-stone-600 dark:text-stone-300" role="status">
      {{ feedback() }}
    </p>
    <bs-checkbox
      [ngModel]="confirmed()"
      (ngModelChange)="confirmed.set($event)"
      [testId]="testId() ? testId() + '-confirmed' : null"
    >
      Ho conservato i codici in un posto sicuro
    </bs-checkbox>
  `,
})
export class RecoveryCodesComponent {
  private readonly document = inject(DOCUMENT);

  readonly codes = input.required<readonly string[]>();
  readonly confirmed = model(false);
  readonly testId = input<string | null>(null);

  protected readonly feedback = signal('');

  protected async copy(): Promise<void> {
    const copied = await copyToClipboard(this.codes().join('\n'));
    this.feedback.set(
      copied ? 'Codici copiati.' : 'Non è stato possibile copiare: usa "Scarica" o "Stampa".',
    );
  }

  protected download(): void {
    const view = this.document.defaultView;
    if (!view?.URL?.createObjectURL) {
      this.feedback.set('Il browser non permette di scaricare il file: usa "Copia" o "Stampa".');
      return;
    }
    const blob = new Blob([recoveryCodesText(this.codes())], { type: 'text/plain;charset=utf-8' });
    const url = view.URL.createObjectURL(blob);
    const link = this.document.createElement('a');
    link.href = url;
    link.download = RECOVERY_CODES_FILE_NAME;
    link.hidden = true;
    this.document.body.appendChild(link);
    link.click();
    link.remove();
    view.setTimeout(() => view.URL.revokeObjectURL(url));
    this.feedback.set(`File ${RECOVERY_CODES_FILE_NAME} scaricato.`);
  }

  /**
   * Stampa da un riquadro nascosto con i soli codici: la pagina dell'applicazione (menu, testata)
   * non finisce sulla carta. Il contenuto si scrive come testo, mai come HTML.
   */
  protected print(): void {
    const frame = this.document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.style.position = 'fixed';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    this.document.body.appendChild(frame);
    const printWindow = frame.contentWindow;
    const printDocument = frame.contentDocument;
    if (!printWindow || !printDocument || typeof printWindow.print !== 'function') {
      frame.remove();
      return;
    }
    printDocument.documentElement.lang = 'it';
    printDocument.title = 'Codici di recupero';
    const pre = printDocument.createElement('pre');
    pre.textContent = recoveryCodesText(this.codes());
    pre.style.font = '14pt monospace';
    pre.style.lineHeight = '1.6';
    printDocument.body.appendChild(pre);
    try {
      printWindow.focus();
      printWindow.print();
    } catch {
      this.feedback.set('Il browser non permette di stampare: usa "Copia" o "Scarica".');
    } finally {
      // Si toglie dopo la chiusura della finestra di stampa (che blocca fino ad allora).
      this.document.defaultView?.setTimeout(() => frame.remove(), 1000);
    }
  }
}
